import { forwardRef, memo, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useTheme } from '@mui/material/styles';
import { Box, DialogActions, DialogContent, DialogTitle, IconButton, Slide, Stack, Typography } from '@mui/material';
import { MIcon } from '../@material-extend';
import LoadingScreen from '../loadings/LoadingScreen';

import { DialogAnimate } from '../animate';
import { setStatusBarStyle } from '../../contexts/SettingsContext/helpers/settingsContextHelpers';
import { useBreakpoints } from '../../hooks/useBreakpoints';
// import useStatusBarStyle from '../../hooks/useStatusBarStyle';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const GenericModal = (props) => {
  const {
    actions = null,
    alignActions = { xs: 'flex-end', sm: 'flex-start' },
    children = null,
    headerActions = null,
    contentGap = null,
    dataCy,
    dividers = false,
    fullScreen = false,
    fullWidth = true,
    icon = null,
    justifyChildren = 'flex-start',
    alignChildren = null,
    loading = false,
    maxWidth = 'xl',
    open = false,
    overflow = 'auto',
    showCloseButton = true,
    showDownload = true,
    subtitle = '',
    sx = {},
    title = '',
    url = '',
    handleClose = null,
    onDownloadFallback,
    ...other
  } = props;

  // useStatusBarStyle(open);
  const { palette, shape } = useTheme();
  const { isLargeMobile } = useBreakpoints();
  const fullScreenAlwaysOnMobile = isLargeMobile ? true : fullScreen;

  useEffect(() => {
    if (open) {
      setStatusBarStyle(palette.background.paper);
    } else {
      setStatusBarStyle(palette.primary.main);
    }

    return () => {
      setStatusBarStyle(palette.primary.main);
    };
  }, [open]);

  const downloadFile = (url) => {
    if (!url.length) {
      return false;
    }

    if (onDownloadFallback) {
      onDownloadFallback();
    }
    window.open(url, '_blank');
    return true;
  };

  return (
    <DialogAnimate
      {...other}
      id="dialog-animate"
      data-cy={dataCy || 'scroll-dialog-title'}
      open={open}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      fullScreen={fullScreenAlwaysOnMobile}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      scroll="paper"
      TransitionComponent={Transition}
      sx={{ ...styleDialog(fullScreenAlwaysOnMobile, shape.borderRadiusSm), ...sx }}
      onClose={handleClose}
    >
      {loading ? (
        <LoadingScreen />
      ) : (
        <>
          <DialogTitle id="scroll-dialog-title" component="div" sx={styleDialogTitle}>
            {icon}
            <Stack flexGrow={1} sx={{ minWidth: 0 }}>
              <Typography variant="title">{title}</Typography>
              {subtitle && <Typography variant="body2">{subtitle}</Typography>}
            </Stack>
            <Box>
              {showDownload && url && !!url.length && (
                <IconButton onClick={() => downloadFile(url)}>
                  <MIcon icon="download" filled />
                </IconButton>
              )}
              {headerActions}
              {handleClose && showCloseButton && (
                <IconButton onClick={handleClose}>
                  <MIcon icon="close" />
                </IconButton>
              )}
            </Box>
          </DialogTitle>

          {children && (
            <DialogContent
              id="modal-details-content-container"
              dividers={dividers}
              sx={styleDialogContent(overflow, contentGap, justifyChildren, alignChildren)}
            >
              {[children]}
            </DialogContent>
          )}

          {actions && (
            <DialogActions id="button-actions" data-cy="button-actions" sx={styleActionsContainer(alignActions)}>
              {actions}
            </DialogActions>
          )}
        </>
      )}
    </DialogAnimate>
  );
};

const styleDialog = (fullScreen, borderRadius) => ({
  '.MuiDialog-paper': {
    borderRadius: fullScreen ? 0 : borderRadius
  }
});

const styleDialogTitle = {
  overflowWrap: 'anywhere',
  whiteSpace: 'normal',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  px: {
    xs: '0.875rem',
    sm: '1rem',
    md: '1.25rem',
    lg: '1.5rem',
    xl: '2rem'
  }
};

const styleDialogContent = (overflow, contentGap, justifyChildren, alignChildren) => ({
  overflow: overflow,
  gap: contentGap,
  justifyContent: justifyChildren,
  alignItems: alignChildren,
  px: {
    xs: '0.875rem',
    sm: '1rem',
    md: '1.25rem',
    lg: '1.5rem',
    xl: '2rem'
  }
});

const styleActionsContainer = (alignActions) => ({
  flexDirection: { xs: 'column', sm: 'row-reverse' },
  alignItems: 'stretch',
  justifyContent: alignActions,
  gap: 1,
  '> :not(:first-of-type)': {
    m: 0 // * To balance "-MuiDialogActions-root>:not(:first-of-type)" { margin-left: 8px; },
  }
});

GenericModal.propTypes = {
  actions: PropTypes.node,
  headerActions: PropTypes.node,
  alignActions: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  children: PropTypes.node,
  contentGap: PropTypes.number,
  dataCy: PropTypes.string,
  dividers: PropTypes.bool,
  fullScreen: PropTypes.bool,
  fullWidth: PropTypes.bool,
  icon: PropTypes.node,
  justifyChildren: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  alignChildren: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  loading: PropTypes.bool,
  maxWidth: PropTypes.string,
  open: PropTypes.bool,
  overflow: PropTypes.string,
  showCloseButton: PropTypes.bool,
  showDownload: PropTypes.bool,
  subtitle: PropTypes.string,
  sx: PropTypes.object,
  title: PropTypes.string,
  url: PropTypes.string,
  handleClose: PropTypes.func,
  onDownloadFallback: PropTypes.func
};

export default memo(GenericModal);
